export interface ReconcileVector2 {
  readonly x: number;
  readonly z: number;
}

export type MinigameFieldMode =
  | 'copy'
  | 'blendNumber'
  | 'blendAngle'
  | 'blendVector2'
  | 'localPredicted'
  | 'visualOnly';

export type MinigameEntityMode =
  | 'authoritative'
  | 'remoteInterpolated'
  | 'localPredicted'
  | 'visualObject';

export interface ReconcileFieldPolicy<TEntity> {
  readonly mode: MinigameFieldMode;
  readonly get: (entity: TEntity) => unknown;
  readonly set: (entity: TEntity, value: unknown) => TEntity;
  readonly alpha?: number;
  readonly snapThreshold?: number;
  readonly ignoreCorrectionThreshold?: number;
}

export interface EntitySyncPolicy<TState, TEntity, TContext = unknown> {
  readonly keyBy: (entity: TEntity) => string;
  readonly mode: MinigameEntityMode;
  readonly getEntities: (state: TState) => ReadonlyArray<TEntity>;
  readonly setEntities: (state: TState, entities: ReadonlyArray<TEntity>) => TState;
  readonly isLocal?: (entity: TEntity, context: ReconcileSnapshotContext<TContext>) => boolean;
  readonly snapWhen?: (
    previous: TEntity | undefined,
    server: TEntity,
    context: ReconcileSnapshotContext<TContext>,
  ) => boolean;
  readonly fields?: ReadonlyArray<ReconcileFieldPolicy<TEntity>>;
}

export interface MinigameInputHistoryEntry<TInput> {
  readonly inputSeq: number;
  readonly roundIndex: number;
  readonly sentAtMs: number;
  readonly durationMs?: number;
  readonly clientTick?: number;
  readonly input: TInput;
}

export interface MinigameInputHistory<TInput> {
  record(
    input: TInput,
    roundIndex: number,
    receipt: {
      readonly inputSeq?: number;
      readonly clientTick?: number;
      readonly sentAtMs?: number;
      readonly durationMs?: number;
    } | void,
  ): void;
  acknowledge(roundIndex: number, inputSeq: number | undefined): void;
  unacknowledged(
    roundIndex: number,
    lastProcessedInputSeq: number | undefined,
  ): ReadonlyArray<MinigameInputHistoryEntry<TInput>>;
  clearBefore(roundIndex: number): void;
  clear(): void;
}

export interface MinigameReplayContext<TContext = unknown> {
  readonly roundIndex?: number;
  readonly localPlayerId?: string;
  readonly nowMs?: number;
  readonly game?: TContext;
}

export interface MinigameSyncPolicy<TState, TInput = unknown, TContext = unknown> {
  readonly phaseKey?: (state: TState) => string | number | null | undefined;
  // Entity policies can target different entity shapes within the same state.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly entityPolicies?: ReadonlyArray<EntitySyncPolicy<TState, any, TContext>>;
  readonly snapWhen?: (
    previous: TState,
    server: TState,
    context: ReconcileSnapshotContext<TContext>,
  ) => boolean;
  readonly replay?: (
    authoritativeState: TState,
    inputs: ReadonlyArray<MinigameInputHistoryEntry<TInput>>,
    context: MinigameReplayContext<TContext>,
  ) => TState;
  readonly applyLiveEvent?: (
    state: TState,
    event: unknown,
    context: ReconcileSnapshotContext<TContext>,
  ) => TState;
  readonly visualClock?: VisualClockOptions;
}

export interface ReconcileSnapshotContext<TContext = unknown> {
  readonly localPlayerId?: string;
  readonly roundIndex?: number;
  readonly previousRoundIndex?: number;
  readonly serverRoundIndex?: number;
  readonly serverSequence?: number;
  readonly lastProcessedInputSeq?: Readonly<Record<string, number>>;
  readonly nowMs?: number;
  readonly game?: TContext;
}

export const LOCAL_RECONCILE_ALPHA = 0.28;
export const REMOTE_RECONCILE_ALPHA = 0.45;
export const OBJECT_RECONCILE_ALPHA = 0.35;
export const LOCAL_PREDICTED_TRANSFORM_RECONCILE_ALPHA = 0.08;
export const LOCAL_PREDICTED_VELOCITY_RECONCILE_ALPHA = 0.16;
export const LOCAL_PREDICTED_TRANSFORM_IGNORE_THRESHOLD = 0.12;
export const LOCAL_PREDICTED_TRANSFORM_SNAP_THRESHOLD = 4;
export const REMOTE_INTERPOLATED_TRANSFORM_SNAP_THRESHOLD = 4;
export const VISUAL_OBJECT_SNAP_DISTANCE = 3.5;

export const LOCAL_PREDICTED_TRANSFORM_RECONCILIATION = {
  alpha: LOCAL_PREDICTED_TRANSFORM_RECONCILE_ALPHA,
  ignoreCorrectionThreshold: LOCAL_PREDICTED_TRANSFORM_IGNORE_THRESHOLD,
  snapThreshold: LOCAL_PREDICTED_TRANSFORM_SNAP_THRESHOLD,
} as const;

export const LOCAL_PREDICTED_VELOCITY_RECONCILIATION = {
  alpha: LOCAL_PREDICTED_VELOCITY_RECONCILE_ALPHA,
} as const;

export const REMOTE_INTERPOLATED_TRANSFORM_RECONCILIATION = {
  alpha: REMOTE_RECONCILE_ALPHA,
  snapThreshold: REMOTE_INTERPOLATED_TRANSFORM_SNAP_THRESHOLD,
} as const;

export const VISUAL_OBJECT_RECONCILIATION = {
  alpha: OBJECT_RECONCILE_ALPHA,
  snapDistance: VISUAL_OBJECT_SNAP_DISTANCE,
} as const;

export const REALTIME_VISUAL_ENTITY_RECONCILIATION = {
  alpha: OBJECT_RECONCILE_ALPHA,
  snapThreshold: VISUAL_OBJECT_SNAP_DISTANCE,
} as const;

export const REALTIME_DEFAULT_RENDER_DELAY_MS = 75;
export const REALTIME_MIN_RENDER_DELAY_MS = 50;
export const REALTIME_MAX_RENDER_DELAY_MS = 75;
export const REALTIME_MAX_GAMEPLAY_REWIND_MS = 150;
export const REALTIME_WORLD_HISTORY_MS = 1_000;

export interface RealtimeClockSample {
  readonly clientReceivedAtMs: number;
  readonly serverNowMs: number;
  readonly clientSentAtMs?: number;
  readonly rttMs?: number;
}

export interface RealtimeClockState {
  readonly offsetMs: number;
  readonly rttEstimateMs: number;
  readonly renderDelayMs: number;
  readonly sampleCount: number;
  readonly rejectedSamples: number;
}

export interface RealtimeClockSync {
  observe(sample: RealtimeClockSample): RealtimeClockState;
  snapshot(): RealtimeClockState;
  estimateServerNow(clientNowMs: number): number;
}

export interface RealtimeCommandTiming {
  readonly inputSeq?: number;
  readonly clientCommandMs?: number;
  readonly receivedAtMs: number;
  readonly estimatedCommandServerMs: number;
  readonly rttEstimateMs?: number;
  readonly renderDelayMs: number;
}

export interface RealtimeCommand<TInput> extends RealtimeCommandTiming {
  readonly playerId: string;
  readonly input: TInput;
}

export interface RealtimeCommandBuffer<TInput> {
  record(playerId: string, input: TInput, metadata?: Partial<RealtimeCommandTiming>): void;
  latestInputs(mapInput?: (input: TInput) => TInput): ReadonlyMap<string, TInput>;
  latestCommands(): ReadonlyMap<string, RealtimeCommand<TInput>>;
  consumeEdges(predicate: (input: TInput) => boolean): ReadonlyArray<RealtimeCommand<TInput>>;
  lastInputSeqByPlayer(): ReadonlyMap<string, number>;
  clear(): void;
}

export interface RealtimeWorldHistory<TState> {
  push(timeMs: number, state: TState): void;
  sample(timeMs: number): TState | null;
  rewind(
    requestedTimeMs: number,
    nowMs: number,
    maxRewindMs?: number,
  ): {
    readonly state: TState | null;
    readonly queryTimeMs: number;
    readonly usedRewindMs: number;
    readonly clamped: boolean;
    readonly reason?: 'too_old' | 'future' | 'empty';
  };
  clear(): void;
}

function finiteNumberOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function createRealtimeClockSync(
  args: {
    readonly initialOffsetMs?: number;
    readonly initialRttMs?: number;
    readonly smoothingAlpha?: number;
    readonly maxSampleRttMs?: number;
    readonly renderDelayMs?: number;
  } = {},
): RealtimeClockSync {
  const alpha = clampNumber(args.smoothingAlpha ?? 0.18, 0.01, 1);
  const maxSampleRttMs = Math.max(1, args.maxSampleRttMs ?? 500);
  let state: RealtimeClockState = {
    offsetMs: args.initialOffsetMs ?? 0,
    rttEstimateMs: Math.max(0, args.initialRttMs ?? REALTIME_DEFAULT_RENDER_DELAY_MS),
    renderDelayMs: clampNumber(
      args.renderDelayMs ?? REALTIME_DEFAULT_RENDER_DELAY_MS,
      REALTIME_MIN_RENDER_DELAY_MS,
      REALTIME_MAX_RENDER_DELAY_MS,
    ),
    sampleCount: 0,
    rejectedSamples: 0,
  };

  return {
    observe(sample): RealtimeClockState {
      const explicitRtt = finiteNumberOrUndefined(sample.rttMs);
      const inferredRtt =
        sample.clientSentAtMs === undefined
          ? undefined
          : Math.max(0, sample.clientReceivedAtMs - sample.clientSentAtMs);
      const rttMs = explicitRtt ?? inferredRtt ?? state.rttEstimateMs;
      const offsetMs = sample.serverNowMs - sample.clientReceivedAtMs;
      const isOutlier =
        !Number.isFinite(offsetMs) ||
        rttMs > maxSampleRttMs ||
        (state.sampleCount > 0 && rttMs > Math.max(120, state.rttEstimateMs * 3));
      if (isOutlier) {
        state = { ...state, rejectedSamples: state.rejectedSamples + 1 };
        return state;
      }
      const nextSampleCount = state.sampleCount + 1;
      const nextOffset =
        state.sampleCount === 0 ? offsetMs : state.offsetMs + (offsetMs - state.offsetMs) * alpha;
      const nextRtt =
        state.sampleCount === 0
          ? rttMs
          : state.rttEstimateMs + (rttMs - state.rttEstimateMs) * alpha;
      state = {
        ...state,
        offsetMs: nextOffset,
        rttEstimateMs: nextRtt,
        sampleCount: nextSampleCount,
      };
      return state;
    },
    snapshot(): RealtimeClockState {
      return state;
    },
    estimateServerNow(clientNowMs): number {
      return clientNowMs + state.offsetMs;
    },
  };
}

export function buildRealtimeCommandTiming(
  metadata: Partial<RealtimeCommandTiming> | undefined,
  nowMs: number,
): RealtimeCommandTiming {
  const receivedAtMs = finiteNumberOrUndefined(metadata?.receivedAtMs) ?? nowMs;
  const clientCommandMs = finiteNumberOrUndefined(metadata?.clientCommandMs);
  const estimatedFromClient =
    clientCommandMs === undefined || finiteNumberOrUndefined(metadata?.estimatedCommandServerMs)
      ? undefined
      : clientCommandMs;
  const estimatedCommandServerMs =
    finiteNumberOrUndefined(metadata?.estimatedCommandServerMs) ??
    estimatedFromClient ??
    receivedAtMs;
  return {
    ...(metadata?.inputSeq === undefined ? {} : { inputSeq: metadata.inputSeq }),
    ...(clientCommandMs === undefined ? {} : { clientCommandMs }),
    receivedAtMs,
    estimatedCommandServerMs: clampNumber(
      estimatedCommandServerMs,
      receivedAtMs - REALTIME_WORLD_HISTORY_MS,
      receivedAtMs + 50,
    ),
    ...(metadata?.rttEstimateMs === undefined ? {} : { rttEstimateMs: metadata.rttEstimateMs }),
    renderDelayMs: clampNumber(
      metadata?.renderDelayMs ?? REALTIME_DEFAULT_RENDER_DELAY_MS,
      REALTIME_MIN_RENDER_DELAY_MS,
      REALTIME_MAX_RENDER_DELAY_MS,
    ),
  };
}

export function createRealtimeCommandBuffer<TInput>(
  now: () => number = () => Date.now(),
): RealtimeCommandBuffer<TInput> {
  const latest = new Map<string, RealtimeCommand<TInput>>();
  let edges: RealtimeCommand<TInput>[] = [];
  const lastInputSeq = new Map<string, number>();

  return {
    record(playerId, input, metadata): void {
      const timing = buildRealtimeCommandTiming(metadata, now());
      const command: RealtimeCommand<TInput> = { playerId, input, ...timing };
      latest.set(playerId, command);
      edges.push(command);
      if (timing.inputSeq !== undefined) lastInputSeq.set(playerId, timing.inputSeq);
      edges = edges.slice(-256);
    },
    latestInputs(mapInput): ReadonlyMap<string, TInput> {
      return new Map(
        [...latest].map(([playerId, command]) => [
          playerId,
          mapInput === undefined ? command.input : mapInput(command.input),
        ]),
      );
    },
    latestCommands(): ReadonlyMap<string, RealtimeCommand<TInput>> {
      return new Map(latest);
    },
    consumeEdges(predicate): ReadonlyArray<RealtimeCommand<TInput>> {
      const consumed: RealtimeCommand<TInput>[] = [];
      const remaining: RealtimeCommand<TInput>[] = [];
      for (const command of edges) {
        if (predicate(command.input)) consumed.push(command);
        else remaining.push(command);
      }
      edges = remaining;
      return consumed;
    },
    lastInputSeqByPlayer(): ReadonlyMap<string, number> {
      return new Map(lastInputSeq);
    },
    clear(): void {
      latest.clear();
      edges = [];
      lastInputSeq.clear();
    },
  };
}

export function createRealtimeWorldHistory<TState>(
  args: {
    readonly interpolate?: (from: TState, to: TState, alpha: number) => TState;
    readonly retentionMs?: number;
  } = {},
): RealtimeWorldHistory<TState> {
  const samples: Array<{ readonly timeMs: number; readonly state: TState }> = [];
  const retentionMs = Math.max(REALTIME_MAX_GAMEPLAY_REWIND_MS, args.retentionMs ?? 1_000);

  function prune(nowMs: number): void {
    const oldest = nowMs - retentionMs;
    while (samples.length > 1 && samples[1]!.timeMs < oldest) samples.shift();
  }

  function sampleAt(timeMs: number): TState | null {
    if (samples.length === 0) return null;
    if (timeMs <= samples[0]!.timeMs) return samples[0]!.state;
    if (timeMs >= samples[samples.length - 1]!.timeMs) return samples[samples.length - 1]!.state;
    for (let index = 1; index < samples.length; index += 1) {
      const to = samples[index]!;
      if (to.timeMs < timeMs) continue;
      const from = samples[index - 1]!;
      if (args.interpolate === undefined) return from.state;
      const alpha = (timeMs - from.timeMs) / Math.max(1, to.timeMs - from.timeMs);
      return args.interpolate(from.state, to.state, clampNumber(alpha, 0, 1));
    }
    return samples[samples.length - 1]!.state;
  }

  return {
    push(timeMs, state): void {
      if (!Number.isFinite(timeMs)) return;
      const previous = samples[samples.length - 1];
      if (previous !== undefined && timeMs <= previous.timeMs) samples.pop();
      samples.push({ timeMs, state });
      prune(timeMs);
    },
    sample(timeMs): TState | null {
      return sampleAt(timeMs);
    },
    rewind(requestedTimeMs, nowMs, maxRewindMs = REALTIME_MAX_GAMEPLAY_REWIND_MS) {
      if (samples.length === 0) {
        return {
          state: null,
          queryTimeMs: requestedTimeMs,
          usedRewindMs: 0,
          clamped: false,
          reason: 'empty' as const,
        };
      }
      const minTimeMs = nowMs - Math.max(0, maxRewindMs);
      const queryTimeMs = clampNumber(requestedTimeMs, minTimeMs, nowMs);
      const reason =
        requestedTimeMs < minTimeMs ? 'too_old' : requestedTimeMs > nowMs ? 'future' : undefined;
      return {
        state: sampleAt(queryTimeMs),
        queryTimeMs,
        usedRewindMs: Math.max(0, nowMs - queryTimeMs),
        clamped: reason !== undefined,
        ...(reason === undefined ? {} : { reason }),
      };
    },
    clear(): void {
      samples.length = 0;
    },
  };
}

export function blendNumber(
  localValue: number,
  serverValue: number,
  alpha = LOCAL_RECONCILE_ALPHA,
): number {
  return localValue + (serverValue - localValue) * alpha;
}

export function blendVector2(
  localValue: ReconcileVector2,
  serverValue: ReconcileVector2,
  alpha = LOCAL_RECONCILE_ALPHA,
): ReconcileVector2 {
  return {
    x: blendNumber(localValue.x, serverValue.x, alpha),
    z: blendNumber(localValue.z, serverValue.z, alpha),
  };
}

export function shortestAngleDelta(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

export function blendAngle(
  localValue: number,
  serverValue: number,
  alpha = LOCAL_RECONCILE_ALPHA,
): number {
  return localValue + shortestAngleDelta(localValue, serverValue) * alpha;
}

export function vectorDistanceSquared(a: ReconcileVector2, b: ReconcileVector2): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return dx * dx + dz * dz;
}

function frameScaledAlpha(alpha: number, deltaSeconds: number): number {
  if (deltaSeconds <= 0) return alpha;
  return 1 - Math.pow(1 - alpha, deltaSeconds / (1 / 60));
}

export type RealtimeVisualFieldMode = 'number' | 'angle' | 'vector2';

export interface RealtimeVisualField<TEntity> {
  readonly mode: RealtimeVisualFieldMode;
  readonly get: (entity: TEntity) => unknown;
  readonly set: (entity: TEntity, value: unknown) => TEntity;
  readonly alpha?: number;
  readonly snapThreshold?: number;
}

export interface RealtimeVisualEntitySmoother<TKey, TEntity> {
  render(
    entities: ReadonlyArray<TEntity>,
    deltaSeconds?: number,
    options?: {
      readonly snap?: boolean;
      readonly immediate?: (entity: TEntity) => boolean;
    },
  ): ReadonlyArray<TEntity>;
  reset(key?: TKey): void;
}

export interface RealtimeLocalPredictedPresentation<TKey, TEntity> {
  render(
    entities: ReadonlyArray<TEntity>,
    options: {
      readonly deltaSeconds: number;
      readonly lifecycleKey: string | number;
      readonly isLocal: (entity: TEntity) => boolean;
      readonly snap?: boolean;
    },
  ): ReadonlyArray<TEntity>;
  reset(key?: TKey): void;
}

export interface RealtimePresentationBuffer<TKey, TEntity> {
  push(
    entities: ReadonlyArray<TEntity>,
    sampleTimeMs: number,
    options?: { readonly snap?: boolean },
  ): void;
  render(nowMs: number, options?: RealtimePresentationRenderOptions): ReadonlyArray<TEntity>;
  reset(key?: TKey): void;
}

export interface RealtimePresentationRenderOptions {
  readonly interpolationDelayMs?: number;
  readonly maxExtrapolationMs?: number;
}

interface RealtimePresentationSample<TEntity> {
  readonly timeMs: number;
  readonly entity: TEntity;
}

const DEFAULT_PRESENTATION_INTERPOLATION_DELAY_MS = 100;
const DEFAULT_PRESENTATION_MAX_EXTRAPOLATION_MS = 120;

function shouldSnapVisualField(
  previousValue: unknown,
  targetValue: unknown,
  snapThreshold: number | undefined,
): boolean {
  if (snapThreshold === undefined) return false;
  if (typeof previousValue === 'number' && typeof targetValue === 'number') {
    return Math.abs(targetValue - previousValue) > snapThreshold;
  }
  if (isVector2(previousValue) && isVector2(targetValue)) {
    return vectorDistanceSquared(previousValue, targetValue) > snapThreshold * snapThreshold;
  }
  return false;
}

function smoothVisualField<TEntity>(
  entity: TEntity,
  previous: TEntity,
  field: RealtimeVisualField<TEntity>,
  deltaSeconds: number,
  alphaOverride?: number,
  snapThresholdOverride?: number,
): TEntity {
  const previousValue = field.get(previous);
  const targetValue = field.get(entity);
  const alpha = frameScaledAlpha(
    alphaOverride ?? field.alpha ?? REALTIME_VISUAL_ENTITY_RECONCILIATION.alpha,
    deltaSeconds,
  );
  const snapThreshold =
    snapThresholdOverride ??
    field.snapThreshold ??
    REALTIME_VISUAL_ENTITY_RECONCILIATION.snapThreshold;
  if (shouldSnapVisualField(previousValue, targetValue, snapThreshold)) {
    return field.set(entity, targetValue);
  }
  if (
    field.mode === 'number' &&
    typeof previousValue === 'number' &&
    typeof targetValue === 'number'
  ) {
    return field.set(entity, blendNumber(previousValue, targetValue, alpha));
  }
  if (
    field.mode === 'angle' &&
    typeof previousValue === 'number' &&
    typeof targetValue === 'number'
  ) {
    return field.set(entity, blendAngle(previousValue, targetValue, alpha));
  }
  if (field.mode === 'vector2' && isVector2(previousValue) && isVector2(targetValue)) {
    return field.set(entity, blendVector2(previousValue, targetValue, alpha));
  }
  return field.set(entity, targetValue);
}

function hasNonFiniteVisualValue(value: unknown): boolean {
  if (typeof value === 'number') return !Number.isFinite(value);
  if (isVector2(value)) return !Number.isFinite(value.x) || !Number.isFinite(value.z);
  return false;
}

function shouldSnapLocalPredictedPresentation<TEntity>(
  previous: TEntity,
  target: TEntity,
  fields: ReadonlyArray<RealtimeVisualField<TEntity>>,
  correctionSnapThreshold: number,
): boolean {
  for (const field of fields) {
    const previousValue = field.get(previous);
    const targetValue = field.get(target);
    if (hasNonFiniteVisualValue(previousValue) || hasNonFiniteVisualValue(targetValue)) {
      return true;
    }
    if (
      shouldSnapVisualField(
        previousValue,
        targetValue,
        field.snapThreshold ?? correctionSnapThreshold,
      )
    ) {
      return true;
    }
  }
  return false;
}

export function createRealtimeEntityVisualSmoother<TKey, TEntity>(args: {
  readonly keyBy: (entity: TEntity) => TKey;
  readonly fields: ReadonlyArray<RealtimeVisualField<TEntity>>;
}): RealtimeVisualEntitySmoother<TKey, TEntity> {
  const previousVisuals = new Map<TKey, TEntity>();
  return {
    render(entities, deltaSeconds = 1 / 60, options = {}): ReadonlyArray<TEntity> {
      const seen = new Set<TKey>();
      const visuals = entities.map((entity) => {
        const key = args.keyBy(entity);
        seen.add(key);
        const previous = previousVisuals.get(key);
        if (
          previous === undefined ||
          options.snap === true ||
          options.immediate?.(entity) === true
        ) {
          previousVisuals.set(key, entity);
          return entity;
        }
        let visual = entity;
        for (const field of args.fields) {
          visual = smoothVisualField(visual, previous, field, deltaSeconds);
        }
        previousVisuals.set(key, visual);
        return visual;
      });
      for (const key of previousVisuals.keys()) {
        if (!seen.has(key)) previousVisuals.delete(key);
      }
      return visuals;
    },
    reset(key): void {
      if (key === undefined) previousVisuals.clear();
      else previousVisuals.delete(key);
    },
  };
}

export function createRealtimeLocalPredictedPresentation<TKey, TEntity>(args: {
  readonly keyBy: (entity: TEntity) => TKey;
  readonly fields: ReadonlyArray<RealtimeVisualField<TEntity>>;
  readonly localAlpha?: number;
  readonly correctionSnapThreshold?: number;
  readonly remoteSnapThreshold?: number;
  readonly hardSnapWhen?: (
    previousVisual: TEntity | undefined,
    target: TEntity,
    context: { readonly lifecycleChanged: boolean; readonly isLocal: boolean },
  ) => boolean;
}): RealtimeLocalPredictedPresentation<TKey, TEntity> {
  const previousVisuals = new Map<TKey, TEntity>();
  let previousLifecycleKey: string | number | undefined;
  const correctionSnapThreshold =
    args.correctionSnapThreshold ?? REALTIME_VISUAL_ENTITY_RECONCILIATION.snapThreshold;
  const remoteSnapThreshold = args.remoteSnapThreshold ?? Number.POSITIVE_INFINITY;

  return {
    render(entities, options): ReadonlyArray<TEntity> {
      const lifecycleChanged =
        previousLifecycleKey !== undefined && previousLifecycleKey !== options.lifecycleKey;
      if (lifecycleChanged || options.snap === true) previousVisuals.clear();
      previousLifecycleKey = options.lifecycleKey;

      const seen = new Set<TKey>();
      const visuals = entities.map((entity) => {
        const key = args.keyBy(entity);
        seen.add(key);
        const isLocal = options.isLocal(entity);
        const previous = previousVisuals.get(key);
        const shouldSnap =
          previous === undefined ||
          lifecycleChanged ||
          options.snap === true ||
          args.hardSnapWhen?.(previous, entity, { lifecycleChanged, isLocal }) === true ||
          (isLocal &&
            previous !== undefined &&
            shouldSnapLocalPredictedPresentation(
              previous,
              entity,
              args.fields,
              correctionSnapThreshold,
            ));
        if (shouldSnap) {
          previousVisuals.set(key, entity);
          return entity;
        }

        let visual = entity;
        for (const field of args.fields) {
          visual = smoothVisualField(
            visual,
            previous,
            field,
            options.deltaSeconds,
            isLocal ? args.localAlpha : undefined,
            field.snapThreshold ?? (isLocal ? correctionSnapThreshold : remoteSnapThreshold),
          );
        }
        previousVisuals.set(key, visual);
        return visual;
      });
      for (const key of previousVisuals.keys()) {
        if (!seen.has(key)) previousVisuals.delete(key);
      }
      return visuals;
    },
    reset(key): void {
      if (key === undefined) {
        previousVisuals.clear();
        previousLifecycleKey = undefined;
      } else {
        previousVisuals.delete(key);
      }
    },
  };
}

function interpolatePresentationField<TEntity>(
  entity: TEntity,
  from: TEntity,
  to: TEntity,
  field: RealtimeVisualField<TEntity>,
  t: number,
): TEntity {
  const fromValue = field.get(from);
  const toValue = field.get(to);
  if (shouldSnapVisualField(fromValue, toValue, field.snapThreshold)) {
    return field.set(entity, toValue);
  }
  if (field.mode === 'number' && typeof fromValue === 'number' && typeof toValue === 'number') {
    return field.set(entity, blendNumber(fromValue, toValue, t));
  }
  if (field.mode === 'angle' && typeof fromValue === 'number' && typeof toValue === 'number') {
    return field.set(entity, fromValue + shortestAngleDelta(fromValue, toValue) * t);
  }
  if (field.mode === 'vector2' && isVector2(fromValue) && isVector2(toValue)) {
    return field.set(entity, {
      x: blendNumber(fromValue.x, toValue.x, t),
      z: blendNumber(fromValue.z, toValue.z, t),
    });
  }
  return field.set(entity, toValue);
}

function renderPresentationEntity<TEntity>(
  samples: ReadonlyArray<RealtimePresentationSample<TEntity>>,
  renderTimeMs: number,
  maxExtrapolationMs: number,
  fields: ReadonlyArray<RealtimeVisualField<TEntity>>,
): TEntity | null {
  if (samples.length === 0) return null;
  if (samples.length === 1) return samples[0]!.entity;

  let from = samples[0]!;
  let to = samples[samples.length - 1]!;
  for (let index = 1; index < samples.length; index += 1) {
    const candidate = samples[index]!;
    if (candidate.timeMs >= renderTimeMs) {
      from = samples[index - 1]!;
      to = candidate;
      break;
    }
  }

  if (renderTimeMs >= to.timeMs) {
    from = samples[Math.max(0, samples.length - 2)]!;
    to = samples[samples.length - 1]!;
  }

  const sampleDuration = Math.max(1, to.timeMs - from.timeMs);
  const extrapolationMs = Math.max(0, renderTimeMs - to.timeMs);
  const clampedRenderTime =
    extrapolationMs > 0 ? to.timeMs + Math.min(maxExtrapolationMs, extrapolationMs) : renderTimeMs;
  const t = Math.max(
    0,
    Math.min(
      1 + maxExtrapolationMs / sampleDuration,
      (clampedRenderTime - from.timeMs) / sampleDuration,
    ),
  );
  let visual = to.entity;
  for (const field of fields) {
    visual = interpolatePresentationField(visual, from.entity, to.entity, field, t);
  }
  return visual;
}

export function createRealtimeEntityPresentationBuffer<TKey, TEntity>(args: {
  readonly keyBy: (entity: TEntity) => TKey;
  readonly fields: ReadonlyArray<RealtimeVisualField<TEntity>>;
  readonly maxSamples?: number;
}): RealtimePresentationBuffer<TKey, TEntity> {
  const tracks = new Map<TKey, RealtimePresentationSample<TEntity>[]>();
  const maxSamples = Math.max(2, args.maxSamples ?? 6);
  return {
    push(entities, sampleTimeMs, options = {}): void {
      const seen = new Set<TKey>();
      for (const entity of entities) {
        const key = args.keyBy(entity);
        seen.add(key);
        const samples =
          options.snap === true
            ? []
            : (tracks.get(key) ?? []).filter((sample) => sample.timeMs < sampleTimeMs);
        samples.push({ timeMs: sampleTimeMs, entity });
        tracks.set(key, samples.slice(-maxSamples));
      }
      for (const key of tracks.keys()) {
        if (!seen.has(key)) tracks.delete(key);
      }
    },
    render(nowMs, options = {}): ReadonlyArray<TEntity> {
      const renderTimeMs =
        nowMs - (options.interpolationDelayMs ?? DEFAULT_PRESENTATION_INTERPOLATION_DELAY_MS);
      const maxExtrapolationMs =
        options.maxExtrapolationMs ?? DEFAULT_PRESENTATION_MAX_EXTRAPOLATION_MS;
      const rendered: TEntity[] = [];
      for (const samples of tracks.values()) {
        const visual = renderPresentationEntity(
          samples,
          renderTimeMs,
          maxExtrapolationMs,
          args.fields,
        );
        if (visual !== null) rendered.push(visual);
      }
      return rendered;
    },
    reset(key): void {
      if (key === undefined) tracks.clear();
      else tracks.delete(key);
    },
  };
}

export function createMinigameInputHistory<TInput>(
  now: () => number = () => (typeof performance === 'undefined' ? Date.now() : performance.now()),
): MinigameInputHistory<TInput> {
  let entries: MinigameInputHistoryEntry<TInput>[] = [];
  const lastRecordedAtMsByRound = new Map<number, number>();

  return {
    record(input, roundIndex, receipt): void {
      if (receipt === undefined || receipt.inputSeq === undefined) return;
      const inputSeq = receipt.inputSeq;
      const commandReceipt = receipt;
      const sentAtMs = commandReceipt.sentAtMs ?? now();
      const previousAtMs = lastRecordedAtMsByRound.get(roundIndex);
      lastRecordedAtMsByRound.set(roundIndex, sentAtMs);
      const durationMs =
        commandReceipt.durationMs ??
        (previousAtMs === undefined ? 0 : Math.max(0, Math.min(250, sentAtMs - previousAtMs)));
      entries.push({
        inputSeq,
        roundIndex,
        sentAtMs,
        durationMs,
        clientTick: commandReceipt.clientTick,
        input,
      });
      entries = entries.slice(-128);
    },
    acknowledge(roundIndex, inputSeq): void {
      if (inputSeq === undefined) return;
      entries = entries.filter(
        (entry) => entry.roundIndex > roundIndex || entry.inputSeq > inputSeq,
      );
    },
    unacknowledged(
      roundIndex,
      lastProcessedInputSeq,
    ): ReadonlyArray<MinigameInputHistoryEntry<TInput>> {
      return entries
        .filter(
          (entry) =>
            entry.roundIndex === roundIndex &&
            (lastProcessedInputSeq === undefined || entry.inputSeq > lastProcessedInputSeq),
        )
        .sort((a, b) => a.inputSeq - b.inputSeq);
    },
    clearBefore(roundIndex): void {
      entries = entries.filter((entry) => entry.roundIndex >= roundIndex);
      for (const recordedRoundIndex of lastRecordedAtMsByRound.keys()) {
        if (recordedRoundIndex < roundIndex) lastRecordedAtMsByRound.delete(recordedRoundIndex);
      }
    },
    clear(): void {
      entries = [];
      lastRecordedAtMsByRound.clear();
    },
  };
}

function valueDistanceSquared(a: unknown, b: unknown): number | null {
  if (typeof a === 'number' && typeof b === 'number') {
    const delta = a - b;
    return delta * delta;
  }
  if (isVector2(a) && isVector2(b)) return vectorDistanceSquared(a, b);
  return null;
}

function isVector2(value: unknown): value is ReconcileVector2 {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ReconcileVector2).x === 'number' &&
    typeof (value as ReconcileVector2).z === 'number'
  );
}

function reconcileField<TEntity>(
  previousEntity: TEntity,
  serverEntity: TEntity,
  field: ReconcileFieldPolicy<TEntity>,
  alpha: number | undefined,
  options: { readonly localPredictedOwner?: boolean } = {},
): TEntity {
  const previousValue = field.get(previousEntity);
  const serverValue = field.get(serverEntity);
  if (field.mode === 'copy') return field.set(serverEntity, serverValue);

  const ignoreThreshold = field.ignoreCorrectionThreshold;
  if (ignoreThreshold !== undefined && options.localPredictedOwner !== false) {
    const distanceSquared = valueDistanceSquared(previousValue, serverValue);
    if (distanceSquared !== null && distanceSquared <= ignoreThreshold * ignoreThreshold) {
      return field.set(serverEntity, previousValue);
    }
  }

  const blendAlpha = field.alpha ?? alpha ?? LOCAL_RECONCILE_ALPHA;
  if (
    (field.mode === 'blendNumber' ||
      field.mode === 'localPredicted' ||
      field.mode === 'visualOnly') &&
    typeof previousValue === 'number' &&
    typeof serverValue === 'number'
  ) {
    return field.set(serverEntity, blendNumber(previousValue, serverValue, blendAlpha));
  }
  if (
    field.mode === 'blendAngle' &&
    typeof previousValue === 'number' &&
    typeof serverValue === 'number'
  ) {
    return field.set(serverEntity, blendAngle(previousValue, serverValue, blendAlpha));
  }
  if (
    (field.mode === 'blendVector2' ||
      field.mode === 'localPredicted' ||
      field.mode === 'visualOnly') &&
    isVector2(previousValue) &&
    isVector2(serverValue)
  ) {
    return field.set(serverEntity, blendVector2(previousValue, serverValue, blendAlpha));
  }
  return field.set(serverEntity, serverValue);
}

function shouldSnapField<TEntity>(
  previousEntity: TEntity,
  serverEntity: TEntity,
  field: ReconcileFieldPolicy<TEntity>,
): boolean {
  if (field.snapThreshold === undefined) return false;
  const distanceSquared = valueDistanceSquared(field.get(previousEntity), field.get(serverEntity));
  return distanceSquared !== null && distanceSquared > field.snapThreshold * field.snapThreshold;
}

export function reconcileMinigameSnapshot<TState, TInput = unknown, TContext = unknown>(
  policy: MinigameSyncPolicy<TState, TInput, TContext>,
  previousState: TState,
  serverState: TState,
  context: ReconcileSnapshotContext<TContext> = {},
): TState {
  if (policy.phaseKey?.(previousState) !== policy.phaseKey?.(serverState)) return serverState;
  if (policy.snapWhen?.(previousState, serverState, context) === true) return serverState;

  let reconciled = serverState;
  for (const entityPolicy of policy.entityPolicies ?? []) {
    const previousEntities = new Map(
      entityPolicy.getEntities(previousState).map((entity) => [entityPolicy.keyBy(entity), entity]),
    );
    const nextEntities: unknown[] = [];
    let hardSnap = false;

    for (const serverEntity of entityPolicy.getEntities(serverState)) {
      const previousEntity = previousEntities.get(entityPolicy.keyBy(serverEntity));
      if (entityPolicy.snapWhen?.(previousEntity, serverEntity, context) === true) {
        hardSnap = true;
        break;
      }
      if (previousEntity === undefined || entityPolicy.mode === 'authoritative') {
        nextEntities.push(serverEntity);
        continue;
      }
      if (
        (entityPolicy.fields ?? []).some((field) =>
          shouldSnapField(previousEntity, serverEntity, field),
        )
      ) {
        hardSnap = true;
        break;
      }

      const entityIsLocal = entityPolicy.isLocal?.(serverEntity, context) === true;
      const defaultAlpha =
        entityPolicy.mode === 'remoteInterpolated'
          ? REMOTE_RECONCILE_ALPHA
          : entityPolicy.mode === 'visualObject'
            ? OBJECT_RECONCILE_ALPHA
            : LOCAL_RECONCILE_ALPHA;
      const alpha =
        entityPolicy.mode === 'localPredicted' && !entityIsLocal
          ? REMOTE_RECONCILE_ALPHA
          : entityIsLocal
            ? LOCAL_RECONCILE_ALPHA
            : defaultAlpha;
      let nextEntity = serverEntity;
      for (const field of entityPolicy.fields ?? []) {
        nextEntity = reconcileField(previousEntity, nextEntity, field, alpha, {
          localPredictedOwner: field.mode === 'localPredicted' ? entityIsLocal : undefined,
        });
      }
      nextEntities.push(nextEntity);
    }

    if (hardSnap) return serverState;
    reconciled = entityPolicy.setEntities(reconciled, nextEntities);
  }

  return reconciled;
}

export function replayPredictedInputs<TState, TInput = unknown, TContext = unknown>(
  policy: MinigameSyncPolicy<TState, TInput, TContext>,
  authoritativeState: TState,
  history: MinigameInputHistory<TInput> | undefined,
  lastProcessedInputSeq: number | undefined,
  context: MinigameReplayContext<TContext> = {},
): TState {
  if (policy.replay === undefined || history === undefined || context.roundIndex === undefined) {
    return authoritativeState;
  }
  const inputs = history.unacknowledged(context.roundIndex, lastProcessedInputSeq);
  if (inputs.length === 0) return authoritativeState;
  return policy.replay(authoritativeState, inputs, context);
}

export function applyMinigameLiveEvent<TState, TInput = unknown, TContext = unknown>(
  policy: MinigameSyncPolicy<TState, TInput, TContext>,
  state: TState,
  event: unknown,
  context: ReconcileSnapshotContext<TContext> = {},
): TState {
  return policy.applyLiveEvent?.(state, event, context) ?? state;
}

export function authoritativeSnapshotPolicy<TState>(): MinigameSyncPolicy<TState, never, never> {
  return {
    snapWhen: () => true,
  };
}

export interface VisualObjectSmoother<TKey, TObject extends ReconcileVector2> {
  smooth(key: TKey, target: TObject, alpha?: number, snapDistance?: number): TObject;
  reset(key?: TKey): void;
}

export function createVisualObjectSmoother<
  TKey,
  TObject extends ReconcileVector2,
>(): VisualObjectSmoother<TKey, TObject> {
  const positions = new Map<TKey, TObject>();
  return {
    smooth(
      key,
      target,
      alpha: number = VISUAL_OBJECT_RECONCILIATION.alpha,
      snapDistance: number = VISUAL_OBJECT_RECONCILIATION.snapDistance,
    ): TObject {
      const current = positions.get(key);
      const next =
        current === undefined ||
        vectorDistanceSquared(current, target) > snapDistance * snapDistance
          ? target
          : ({ ...target, ...blendVector2(current, target, alpha) } as TObject);
      positions.set(key, next);
      return next;
    },
    reset(key): void {
      if (key === undefined) positions.clear();
      else positions.delete(key);
    },
  };
}

export interface VisualClockOptions {
  readonly alpha?: number;
  readonly maxDeltaMs?: number;
  readonly maxLeadMs?: number;
  readonly snapThresholdMs?: number;
}

export const VISUAL_CLOCK_RECONCILIATION = {
  maxLeadMs: 150,
  snapThresholdMs: 650,
} as const satisfies VisualClockOptions;

export function advanceVisualClock(
  currentVisualMs: number,
  authoritativeMs: number,
  deltaMs = 0,
  options: VisualClockOptions = {},
): number {
  const safeAuthoritative = Number.isFinite(authoritativeMs) ? authoritativeMs : 0;
  const safeCurrent = Number.isFinite(currentVisualMs) ? currentVisualMs : safeAuthoritative;
  const snapThresholdMs = options.snapThresholdMs ?? 750;
  if (Math.abs(safeCurrent - safeAuthoritative) > snapThresholdMs) return safeAuthoritative;

  const safeDelta = Math.max(0, Math.min(deltaMs, options.maxDeltaMs ?? 100));
  const advanced = safeCurrent + safeDelta;
  if (advanced < safeAuthoritative) {
    return blendNumber(advanced, safeAuthoritative, options.alpha ?? OBJECT_RECONCILE_ALPHA);
  }

  const maxLeadMs = options.maxLeadMs ?? 150;
  if (advanced > safeAuthoritative + maxLeadMs) return safeCurrent;
  return advanced;
}
